import { InputRef, Modal } from 'antd';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IApiHistoryCallerIdParams } from 'services/types/caller';
import styled from 'styled-components';
import { ICustomerUI, IUICallerIdHistoryItem } from 'types/customer';
import CallerIdHistoryTable from './CallerIdHistoryTable';
import DatePicker from './DatePicker';
import CallerIdSummary from './CallerIdSummary';
import InputSearchByName from 'AppointmentBox/CustomerTab/InputSearchByName';
import Box from 'components/Box';
import iconSearch from './assets/search.svg';
import iconClear from './assets/clear.svg';
import { formatPhone } from 'utils/formatPhone';
type Props = {
  visible?: boolean;
  onClose: () => void;
  params: IApiHistoryCallerIdParams;
  onChange: (params: Partial<IApiHistoryCallerIdParams>) => void;
  dataSource: IUICallerIdHistoryItem[];
  loading?: boolean;
  totalItems: number;
  onChangePage: (page: number) => void;
  customer?: ICustomerUI | null;
  onSelectCustomer: (o: ICustomerUI | null) => void;
};
export type ICallerIdHistoryModalRef = {
  close: () => void;
};
export const callerIdHistoryModalRef = React.createRef<ICallerIdHistoryModalRef>();
const CallerIdHistoryModal = forwardRef<ICallerIdHistoryModalRef, Props>((props, ref) => {

  const { visible, onClose, dataSource, params, onChange, loading, totalItems, onChangePage, customer, onSelectCustomer } = props;
  useImperativeHandle(ref, () => ({
    close: () => {
      onClose();
    }
  }));

  return (
    <Modal styles={{ content: { zoom: .8 } }} centered open={visible} footer={<></>} closable={false} closeIcon={null} width={'1100px'} destroyOnClose={false}>
      <Container>
        <Header>
          <span>History Caller ID</span>
          <button style={{ right: 16, position: 'absolute', bottom: 0, top: 0 }} onClick={onClose}><p className="text-action">CLOSE</p></button>
        </Header>
        <Body>
          <Filter>
            <InputSearchCustomer onSelect={onSelectCustomer} customer={customer} />
            <DatePicker params={params} onChange={onChange} />
          </Filter>
          <CallerIdSummary />
          <CallerIdHistoryTable
            params={params}
            loading={loading}
            dataSource={dataSource}
            totalItems={totalItems}
            onChangePage={onChangePage}
          />
        </Body>
        <Footer>
          <button onClick={onClose} type='button' className="btn"><span>CLOSE</span></button>
        </Footer>
      </Container>
    </Modal>
  );
});
CallerIdHistoryModal.displayName = 'CallerIdHistoryModal';
export default CallerIdHistoryModal;

const InputSearchCustomer = ({ onSelect, customer }: { onSelect: (o: ICustomerUI | null) => void, customer?: ICustomerUI | null }) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const onClear = () => {
    onSelect(null);
    setTimeout(() => {
      inputRef.current?.focus();
      setOpen(true);
    }, 100);
  };
  return (
    <InputSearchCustomerStyled flex={0.8} className={customer?.id ? 'customer-selected' : ''}>
      <InputSearchByName key={customer?.id} placeholder={customer ? `${customer.name} - ${formatPhone(customer.phone)}` : 'Customer name/phone'} disabledAddCustomer open={open} setOpen={setOpen} inputRef={inputRef} onSelect={onSelect}
      />
      {customer ? <SuffixView type='button' onClick={onClear}><img src={iconClear} alt='clear' style={{ width: 24, height: 24 }} /></SuffixView> : <SuffixView type='button'><img src={iconSearch} alt='search' style={{ width: 24, height: 24 }} /></SuffixView>}
    </InputSearchCustomerStyled>
  );
};
const SuffixView = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 56px;
  height: 56px;
`;
const InputSearchCustomerStyled = styled(Box)`
  position: relative;
  &.customer-selected {
    .InputSearchStyled {
      .ant-input {
        &::placeholder {
          color: #1D2129;
        }
      }
    }
  }
  .InputSearchStyled {
    align-self: stretch;
    .ant-input {
      height: 56px;
      font-size: 1.2rem;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  background: #F8F9FE;
`;
const Header = styled.div`
  display: flex;
  height: 48px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border-bottom: 1px solid #148CF1;
  background: #E8F3FF;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
  position: relative;
  span {
    align-self: stretch;
    color: #1D2129;
    text-align: center;
    font-family: Poppins;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
  }
  .text-action {
    color: #F05326;
    font-family: Poppins;
    font-size: 22px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 133.333% */
    white-space: nowrap;
  }
`;
const Body = styled.div`
  display: flex;
  padding: 32px 24px 16px 24px;
  align-items: center;
  flex-direction: column;
  gap: 24px;
  align-self: stretch;
  background: #fff;
`;

const Filter = styled.div`
  display: flex;
  align-self: stretch;
  gap: 1.5rem;
`;

const Footer = styled.div`
  display: flex;
  padding: 24px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  button.btn {
    display: flex;
    padding: 16px 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 4px;
    background: #CCD4DC;
    box-shadow: 0 4px 8px -3px rgba(45, 35, 66, 0.30);

    &:active {
      opacity: 0.6;
    }

    span {
      color: #1D2129;
      text-align: center;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }

    &.btn-submit {
      background: #00A140;
      span {
        color: #ffffff;
      }
    }
  }
`;
